// Damage pipeline and hit resolution.
// Conforms to GDD §4.1.

import type { EntityBase, Player, Enemy } from '../types.js';
import type { RngService } from './rng.js';
import type { WorldState } from './state.js';
import { getUpgradeMods } from './upgrade-system.js';
import { getTraitMods } from './loadout.js';

export interface CombatStats {
  flat_damage: number;
  increased_damage_pct: number;
  more_damage_mult: number[];
  crit_chance: number;
  crit_mult_bonus: number;
  lumensurge_multiplier: number;
}

export interface HitResult {
  damage: number;
  crit: boolean;
}

export function defaultCombatStats(): CombatStats {
  return {
    flat_damage: 0,
    increased_damage_pct: 0,
    more_damage_mult: [],
    crit_chance: 0.05,
    crit_mult_bonus: 0,
    lumensurge_multiplier: 1
  };
}

export function playerCombatStats(player: Player): CombatStats {
  // Lumensurge upgrade mods feed the damage pipeline (GDD §4.1): damageMult
  // goes into the additive "increased" bucket, critChanceBonus adds to the
  // base 5% crit chance. The loadout STRIKER trait damage joins the same
  // increased bucket. Ablative Crown (hull_damage_link) also joins the
  // increased bucket, but only while hull remains. The rest stays at Gate 1
  // defaults.
  const mods = getUpgradeMods(player);
  const traits = getTraitMods(player);
  const hullLink = player.hull > 0 ? mods.hullDamageLinkMult : 0;
  return {
    flat_damage: 0,
    increased_damage_pct: mods.damageMult + traits.damageMult + hullLink,
    more_damage_mult: [],
    crit_chance: 0.05 + mods.critChanceBonus + player.boon_crit_chance,
    crit_mult_bonus: 0,
    lumensurge_multiplier: player.lumensurge_tier
  };
}

export function resolveHit(
  baseDamage: number,
  attacker: CombatStats,
  targetMitigation: number,
  rng: RngService
): HitResult {
  // 1. base
  let dmg = baseDamage;
  // 2. additive
  dmg += attacker.flat_damage;
  dmg *= (1 + attacker.increased_damage_pct);
  // 3. multipliers
  for (const m of attacker.more_damage_mult) {
    dmg *= m;
  }
  dmg *= attacker.lumensurge_multiplier;
  // 4. crit
  const cc = Math.min(attacker.crit_chance, 0.75);
  let crit = false;
  if (cc > 0 && rng.get('crit').next() < cc) {
    const mult = Math.min(2.0 + attacker.crit_mult_bonus, 4.0);
    dmg *= mult;
    crit = true;
  }
  // 5. mitigation
  const mit = Math.max(0, Math.min(targetMitigation, 0.80));
  dmg *= (1 - mit);
  // 6. rounding
  const damage = Math.max(1, Math.floor(dmg + 0.5));
  return { damage, crit };
}

// ---- Shield pipeline ----
// The energy shield absorbs incoming damage before HP and regenerates after
// SHIELD_REGEN_DELAY_TICKS without taking damage. Fully deterministic: the
// delay and regen run on the fixed sim tick, never the wall clock.

/** Delay before shield regen starts (3 s at 60 Hz). */
export const SHIELD_REGEN_DELAY_TICKS = 180;
/** Shield points regenerated per sim tick (10 per second at 60 Hz). */
export const SHIELD_REGEN_PER_TICK = 10 / 60;

/**
 * Apply damage to the player: shield first, overflow to HP. Records the tick
 * so the regen delay restarts on every hit. Callers still apply their own
 * i-frames/mercy-window gates before invoking this.
 */
export function applyDamageToPlayer(player: Player, damage: number, tick: number): void {
  player.last_damage_tick = tick;
  let remaining = damage;
  if (player.shield > 0) {
    const absorbed = Math.min(player.shield, remaining);
    player.shield -= absorbed;
    remaining -= absorbed;
  }
  if (remaining > 0) {
    player.hp -= remaining;
  }
}

/** Per-tick shield regeneration. No-op without a shield (max_shield 0).
 * `regenMult` carries stance bonuses (Serenity tier 3 doubles regen). */
export function updateShieldRegen(world: WorldState, tick: number, regenMult = 1): void {
  const p = world.player;
  if (!p.alive || p.max_shield <= 0) return;
  if (p.shield >= p.max_shield) return;
  if (tick - p.last_damage_tick < SHIELD_REGEN_DELAY_TICKS) return;
  p.shield = Math.min(p.max_shield, p.shield + SHIELD_REGEN_PER_TICK * p.boon_shield_regen_mult * regenMult);
}

// ---- Ramming ----
// Contact at RAM_SPEED_THRESHOLD or faster is a ram: the player deals ram
// damage to the enemy and the recoil is taken on the hull (blue hearts)
// before HP, bypassing the energy shield. Hull does not regenerate — it is a
// finite resource, restorable only by future pickups/upgrades. Deterministic:
// speed comes from sim state and everything runs on the fixed sim tick.

/** Player speed (u/s) at which enemy contact becomes a ram. */
export const RAM_SPEED_THRESHOLD = 10;
/** Ram damage dealt to the enemy: base + per-speed × player speed. */
export const RAM_DAMAGE_BASE = 10;
export const RAM_DAMAGE_PER_SPEED = 2;

/** Ram damage dealt to the enemy for a given player speed (u/s). */
export function ramDamageForSpeed(speed: number): number {
  return Math.max(1, Math.floor(RAM_DAMAGE_BASE + speed * RAM_DAMAGE_PER_SPEED + 0.5));
}

/**
 * Apply ram recoil damage to the player: hull first, overflow to HP. Unlike
 * applyDamageToPlayer this bypasses the energy shield — the hull is the
 * dedicated buffer for high-speed impacts. Records the tick so the shield/HP
 * regen delays restart on every ram.
 */
export function applyRamDamageToPlayer(player: Player, damage: number, tick: number): void {
  player.last_damage_tick = tick;
  let remaining = damage;
  if (player.hull > 0) {
    const absorbed = Math.min(player.hull, remaining);
    player.hull -= absorbed;
    remaining -= absorbed;
  }
  if (remaining > 0) {
    player.hp -= remaining;
  }
}

export function enemyHpAtTime(baseHp: number, t: number): number {
  return Math.floor(baseHp * Math.pow(1.6, t / 120) + 0.5);
}

export function enemyDamageAtTime(baseDmg: number, t: number): number {
  const mult = Math.min(2.0, Math.pow(1.08, t / 120));
  return Math.floor(baseDmg * mult + 0.5);
}
